import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { TextField, IconButton, InputAdornment, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useUpdateUserMutation } from '../../../features/users/usersApiSlice';
import { useSendLogoutMutation } from '../../../features/auth/authApiSlice';
import useAuth from '../../../features/hooks/useAuth';
import { HeadingWrap, Span } from '../../../Styles/CommonStyles';

// Styled components
const EditFieldButton = styled.button`
  display: inline-flex;
  -webkit-box-align: center;
  -webkit-box-pack: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  cursor: pointer;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-weight: 500;
  font-size: 0.75rem;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  text-transform: uppercase;
  min-width: 64px;
  height: 35px;
  padding: 6px 16px;
  border-radius: 4px;
  border-width: 0;
  background-color: #5C5891;
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
  margin-left: 20px;
  margin-top: 20px;
  color: #fff;
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const LabelInputWrapper = styled.div`
  margin-bottom: 25px;
  flex: 1 0 30%;
  max-width: 400px;
`;

const ValidationTextDiv = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 5px;
`;

const SuccessTextDiv = styled.div`
  color: green;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background-color: #e8f5e8;
  border-radius: 4px;
`;

const ErrorTextDiv = styled.div`
  color: red;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background-color: #ffeaea;
  border-radius: 4px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px;
  max-width: 500px;
`;

const PasswordRequirementsContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const RequirementsTitle = styled(Typography)`
  && {
    font-weight: 600;
    margin-bottom: 15px;
    color: #495057;
    font-size: 16px;
  }
`;

const RequirementsList = styled.ul`
  margin: 0;
  padding-left: 20px;
  font-size: 14px;
  color: #6c757d;
  line-height: 1.6;
  
  li {
    margin-bottom: 8px;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  }
`;

// Password validation regex from EditUsers.jsx
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;

const EditPassword = () => {
  const Auth = useAuth();
  const navigate = useNavigate();
  const userRef = useRef();

  // State for form fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // State for password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // State for validation
  const [validNewPwd, setValidNewPwd] = useState(false);
  const [validMatch, setValidMatch] = useState(false);
  const [newPwdFocus, setNewPwdFocus] = useState(false);
  const [matchFocus, setMatchFocus] = useState(false);

  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // RTK Query mutations
  const [updateUser, { 
    isLoading: isUpdating 
  }] = useUpdateUserMutation();

  const [sendLogout, {
    isSuccess: logoutSuccess,
    isError: logoutError
  }] = useSendLogoutMutation();

  // Validation effects
  useEffect(() => {
    setValidNewPwd(PWD_REGEX.test(newPassword));
  }, [newPassword]);

  useEffect(() => {
    setValidMatch(newPassword === confirmPassword && newPassword !== '');
  }, [newPassword, confirmPassword]);

  // Clear messages when user starts typing
  useEffect(() => {
    if (currentPassword || newPassword || confirmPassword) {
      setErrorMessage('');
      setSuccessMessage('');
    }
  }, [currentPassword, newPassword, confirmPassword]);

  // Handle logout navigation
  useEffect(() => {
    if (logoutSuccess) {
      if (Auth.supportuser) {
        navigate('/supportlogin');
      } else {
        navigate('/');
      }
    }
  }, [logoutSuccess, navigate, Auth.supportuser]);

  // Handle logout error
  useEffect(() => {
    if (logoutError) {
      // Even if logout fails, redirect to home for security
      console.error('Logout failed, but redirecting for security');
      if (Auth.supportuser) {
        navigate('/supportlogin');
      } else {
        navigate('/');
      }
    }
  }, [logoutError, navigate, Auth.supportuser]);

  // Toggle password visibility functions
  const handleToggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setErrorMessage('');
    setSuccessMessage('');

    // Validate form
    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMessage('All fields are required');
      return;
    }

    if (!validNewPwd) {
      setErrorMessage('New password must be 8-24 characters and contain at least one lowercase letter, one uppercase letter, one number, and one special character (!@#$%)');
      return;
    }

    if (!validMatch) {
      setErrorMessage('New password and confirm password do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setErrorMessage('New password must be different from current password');
      return;
    }

    try {
      // Use updateUser with password change request
      await updateUser({
        id: Auth.id,
        email: Auth.email,
        currentPassword: currentPassword,
        password: newPassword,
        passwordChangeRequest: true
      }).unwrap();

      // Success - clear form and show success message
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccessMessage('Password changed successfully! You will be logged out in 3 seconds for security.');

      // Auto-logout after 3 seconds for security
      setTimeout(() => {
        sendLogout();
      }, 3000);

    } catch (error) {
      console.error('Password change error:', error);
      setErrorMessage(error?.data?.message || 'Failed to change password. Please try again.');
    }
  };

  const isFormValid = currentPassword && newPassword && confirmPassword && validNewPwd && validMatch;
  const isLoading = isUpdating;

  return (
    <>
      <HeadingWrap>
        <Span>Edit Password</Span>
      </HeadingWrap>

      <FormContainer>
        <form onSubmit={handleSubmit}>
          {/* Current Password */}
          <LabelInputWrapper>
            <TextField
              id="current-password"
              size="small"
              ref={userRef}
              name="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              onChange={(e) => setCurrentPassword(e.target.value)}
              label="Current Password"
              value={currentPassword}
              variant="outlined"
              fullWidth
              sx={{ backgroundColor: "white" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleCurrentPasswordVisibility}>
                      {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </LabelInputWrapper>

          {/* New Password */}
          <LabelInputWrapper>
            <TextField
              id="new-password"
              size="small"
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              onChange={(e) => setNewPassword(e.target.value)}
              label="New Password"
              value={newPassword}
              variant="outlined"
              fullWidth
              onFocus={() => setNewPwdFocus(true)}
              onBlur={() => setNewPwdFocus(false)}
              sx={{ backgroundColor: "white" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleNewPasswordVisibility}>
                      {showNewPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {newPwdFocus && !validNewPwd && newPassword ? (
              <ValidationTextDiv>
                Password must be 8-24 characters and contain at least one lowercase letter, one uppercase letter, one number, and one special character (!@#$%)
              </ValidationTextDiv>
            ) : null}
          </LabelInputWrapper>

          {/* Confirm Password */}
          <LabelInputWrapper>
            <TextField
              id="confirm-password"
              size="small"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              onChange={(e) => setConfirmPassword(e.target.value)}
              label="Confirm New Password"
              value={confirmPassword}
              variant="outlined"
              fullWidth
              onFocus={() => setMatchFocus(true)}
              onBlur={() => setMatchFocus(false)}
              sx={{ backgroundColor: "white" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleConfirmPasswordVisibility}>
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {matchFocus && !validMatch && confirmPassword ? (
              <ValidationTextDiv>
                Passwords do not match
              </ValidationTextDiv>
            ) : null}
          </LabelInputWrapper>

          {/* Submit Button */}
          <EditFieldButton 
            type="submit" 
            disabled={!isFormValid || isLoading}
          >
            {isLoading ? 'Changing...' : 'Change Password'}
          </EditFieldButton>

          {/* Success Message */}
          {successMessage && (
            <SuccessTextDiv>
              {successMessage}
            </SuccessTextDiv>
          )}

          {/* Error Message */}
          {errorMessage && (
            <ErrorTextDiv>
              {errorMessage}
            </ErrorTextDiv>
          )}
        </form>

        {/* Password Requirements */}
        <PasswordRequirementsContainer>
          <RequirementsTitle variant="subtitle1">
            Password Requirements:
          </RequirementsTitle>
          <RequirementsList>
            <li>8-24 characters long</li>
            <li>At least one lowercase letter (a-z)</li>
            <li>At least one uppercase letter (A-Z)</li>
            <li>At least one number (0-9)</li>
            <li>At least one special character (!@#$%)</li>
            <li>Must be different from current password</li>
          </RequirementsList>
        </PasswordRequirementsContainer>
      </FormContainer>
    </>
  );
};

export default EditPassword;